import { apiFetch } from "./client.js";

export type CadenceStatus = "pending" | "done" | "skipped" | "snoozed" | string;
export type CadencePeriod = "" | "manha" | "tarde" | "noite" | string;

export type CadenceTask = {
  id: number;
  leadId: string;
  scheduledDate: string;
  scheduledPeriod: CadencePeriod;
  status: CadenceStatus;
  note?: string;
  leadName?: string;
  leadPhone?: string;
  leadFunnelId?: string;
  leadStage?: string;
  leadPriority?: string;
  completedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type CadenceDaySummary = {
  date: string;
  pending: number;
  done: number;
  skipped: number;
  snoozed: number;
  late: number;
  total: number;
  tasks: CadenceTask[];
};

export type CadenceMonth = {
  month: string;
  days: CadenceDaySummary[];
};

export type CadenceCreatePayload = {
  leadIds: string[];
  dates?: string[];
  startDate?: string;
  endDate?: string;
  scheduledPeriod?: CadencePeriod;
  note?: string;
};

export type CadencePatchPayload = Partial<{
  scheduledDate: string;
  scheduledPeriod: CadencePeriod;
  status: CadenceStatus;
  note: string;
}>;

export async function fetchCadenceMonth(month: string): Promise<CadenceMonth> {
  const data = await apiFetch(`/cadence/month?month=${encodeURIComponent(month)}`);
  return normalizeCadenceMonth(data, month);
}

export async function fetchTodayCadence(): Promise<CadenceDaySummary> {
  const data = await apiFetch("/cadence/today");
  return normalizeCadenceDay(data);
}

export async function fetchLeadCadence(leadId: string): Promise<CadenceTask[]> {
  const data = await apiFetch(`/cadence/leads/${encodeURIComponent(leadId)}`);
  return Array.isArray(data) ? data.map(normalizeCadenceTask) : [];
}

export async function createCadenceTasks(payload: CadenceCreatePayload): Promise<CadenceTask[]> {
  const data = await apiFetch("/cadence/tasks", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return Array.isArray(data) ? data.map(normalizeCadenceTask) : [];
}

export async function updateCadenceTask(taskId: number, patch: CadencePatchPayload): Promise<CadenceTask> {
  const data = await apiFetch(`/cadence/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return normalizeCadenceTask(data);
}

export async function deleteCadenceTask(taskId: number): Promise<void> {
  await apiFetch(`/cadence/tasks/${taskId}`, { method: "DELETE" });
}

function normalizeCadenceMonth(data: unknown, fallbackMonth: string): CadenceMonth {
  const raw = isRecord(data) ? data : {};
  const days = Array.isArray(raw.days) ? raw.days.map(normalizeCadenceDay) : [];
  return {
    month: String(raw.month || fallbackMonth),
    days,
  };
}

function normalizeCadenceDay(data: unknown): CadenceDaySummary {
  const raw = isRecord(data) ? data : {};
  const tasks = Array.isArray(raw.tasks) ? raw.tasks.map(normalizeCadenceTask) : [];
  return {
    date: String(raw.date || ""),
    pending: Number(raw.pending || 0),
    done: Number(raw.done || 0),
    skipped: Number(raw.skipped || 0),
    snoozed: Number(raw.snoozed || 0),
    late: Number(raw.late || 0),
    total: Number(raw.total || tasks.length),
    tasks,
  };
}

function normalizeCadenceTask(data: unknown): CadenceTask {
  const raw = isRecord(data) ? data : {};
  return {
    id: Number(raw.id || 0),
    leadId: String(raw.leadId || ""),
    scheduledDate: String(raw.scheduledDate || ""),
    scheduledPeriod: String(raw.scheduledPeriod || ""),
    status: String(raw.status || "pending"),
    note: String(raw.note || ""),
    leadName: String(raw.leadName || ""),
    leadPhone: String(raw.leadPhone || ""),
    leadFunnelId: String(raw.leadFunnelId || ""),
    leadStage: String(raw.leadStage || ""),
    leadPriority: String(raw.leadPriority || ""),
    completedAt: raw.completedAt ? String(raw.completedAt) : null,
    createdAt: raw.createdAt ? String(raw.createdAt) : null,
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object");
}
